export const SUPPORT_AGENT_MCP_PATH = "/api/support-agent/mcp";

export const MCP_MODERN_PROTOCOL_VERSIONS = ["2026-07-28"] as const;

export const MCP_LEGACY_PROTOCOL_VERSIONS = [
  "2025-11-25",
  "2025-06-18",
  "2025-03-26",
] as const;

export type McpModernProtocolVersion =
  (typeof MCP_MODERN_PROTOCOL_VERSIONS)[number];
export type McpLegacyProtocolVersion =
  (typeof MCP_LEGACY_PROTOCOL_VERSIONS)[number];
export type McpProtocolVersion =
  | McpModernProtocolVersion
  | McpLegacyProtocolVersion;

export const SUPPORT_AGENT_MCP_PROTOCOL_VERSIONS: readonly McpProtocolVersion[] =
  [...MCP_MODERN_PROTOCOL_VERSIONS, ...MCP_LEGACY_PROTOCOL_VERSIONS];

export const SUPPORT_AGENT_MCP_LATEST_PROTOCOL_VERSION: McpModernProtocolVersion =
  MCP_MODERN_PROTOCOL_VERSIONS[0];

export const SUPPORT_AGENT_MCP_LATEST_LEGACY_VERSION: McpLegacyProtocolVersion =
  MCP_LEGACY_PROTOCOL_VERSIONS[0];

export const MCP_PROTOCOL_VERSION_WITHOUT_HEADER: McpLegacyProtocolVersion =
  "2025-03-26";

export const MCP_META_PROTOCOL_VERSION_KEY =
  "io.modelcontextprotocol/protocolVersion";
export const MCP_META_CLIENT_INFO_KEY = "io.modelcontextprotocol/clientInfo";
export const MCP_META_SERVER_INFO_KEY = "io.modelcontextprotocol/serverInfo";
export const MCP_META_OUTCOME_KEY = "com.repackify/outcome";
export const MCP_META_WARNINGS_KEY = "com.repackify/warnings";

export const SUPPORT_AGENT_KEY_SCOPES = ["read", "tickets:write", "ops:write"] as const;
export type SupportAgentKeyScope = (typeof SUPPORT_AGENT_KEY_SCOPES)[number];
export const LEGACY_SUPPORT_AGENT_KEY_SCOPES: readonly SupportAgentKeyScope[] = ["read", "tickets:write"];

export const SUPPORT_AGENT_IDEMPOTENCY_REQUIRED_FROM = Date.UTC(2026, 10, 2);
export const SUPPORT_AGENT_IDEMPOTENCY_REQUIRED_FROM_LABEL = "2026-11-02";
export const MISSING_IDEMPOTENCY_KEY_WARNING = "missing_idempotency_key";

export const MCP_NOT_FOUND_OUTCOMES: readonly string[] = ["not_found", "unknown_tool"];

export const TOOL_RESULT_TRUNCATION_NOTICE =
  "…[CUT OFF MID-VALUE — this payload exceeded the size limit and is INCOMPLETE. Do not count, total, or summarize it as if it were the full set; re-run with narrower filters or a smaller page.]";

export function parseToolResultText(text: string): unknown {
  const separator = text.indexOf(":\n");
  if (separator === -1) return undefined;
  try {
    const data: unknown = JSON.parse(text.slice(separator + 2));
    return data;
  } catch {
    return undefined;
  }
}

export const MCP_ERROR_UNSUPPORTED_PROTOCOL_VERSION = -32022;
export const MCP_ERROR_HEADER_MISMATCH = -32020;
export const MCP_ERROR_METHOD_NOT_FOUND = -32601;
export const MCP_ERROR_INVALID_PARAMS = -32602;
export const MCP_ERROR_INVALID_REQUEST = -32600;
export const MCP_ERROR_PARSE = -32700;

export function isSupportedMcpProtocolVersion(
  value: string,
): value is McpProtocolVersion {
  return SUPPORT_AGENT_MCP_PROTOCOL_VERSIONS.some(
    (version) => version === value,
  );
}

export function isModernMcpProtocolVersion(value: string): boolean {
  return MCP_MODERN_PROTOCOL_VERSIONS.some((version) => version === value);
}

export const SUPPORT_AGENT_MCP_SERVER = {
  name: "repackify-support",
  version: "3.1.0",
} as const;

export const SUPPORT_AGENT_MCP_TOOLS = {
  listTickets: "support_list_tickets",
  getTicket: "support_get_ticket",
  createTicket: "support_create_ticket",
  claimTicket: "support_claim_ticket",
  updateTicket: "support_update_ticket",
  linkPullRequest: "support_link_pull_request",
  closeTicket: "support_close_ticket",
  listProjects: "support_list_projects",
  createProject: "support_create_project",
  setTicketProject: "support_set_ticket_project",
  getProject: "support_get_project",
  addProjectLink: "support_add_project_link",
  addProjectDocument: "support_add_project_document",
  reopenTicket: "support_reopen_ticket",
  setTicketFields: "support_set_ticket_fields",
  getTicketAttachment: "support_get_ticket_attachment",
  updateProjectLink: "support_update_project_link",
  updateProjectDocument: "support_update_project_document",
  removeProjectResource: "support_remove_project_resource",
  requestAgentFix: "support_request_agent_fix",
  reportAgentFix: "support_report_agent_fix",
} as const;

export const AGENT_CONTEXT_MCP_TOOLS = {
  find: "repackify_find",
  listOrders: "repackify_list_orders",
  getOrder: "repackify_get_order",
  getQuote: "repackify_get_quote",
  getCompany: "repackify_get_company",
  getProduct: "repackify_get_product",
  getContact: "repackify_get_contact",
  getLead: "repackify_get_lead",
  listQuotes: "repackify_list_quotes",
  listLeads: "repackify_list_leads",
  listInvoices: "repackify_list_invoices",
  listBills: "repackify_list_bills",
  listPayments: "repackify_list_payments",
  getShipment: "repackify_get_shipment",
  listEmails: "repackify_list_emails",
  listTestInbox: "repackify_list_test_inbox",
  getTestInboxMessage: "repackify_get_test_inbox_message",
  getOrderActivity: "repackify_get_order_activity",
  getOrderAccounting: "repackify_get_order_accounting",
  investigateOrder: "repackify_investigate_order",
  debugBundle: "repackify_debug_bundle",
  listListings: "repackify_list_listings",
  listCompanies: "repackify_list_companies",
  listContacts: "repackify_list_contacts",
  getUser: "repackify_get_user",
  workerLogs: "repackify_worker_logs",
  convexLogs: "repackify_convex_logs",
  exceptions: "repackify_exceptions",
  webReport: "repackify_web_report",
  convexInsights: "repackify_convex_insights",
  listPhoneCalls: "repackify_list_phone_calls",
  listMailThreads: "repackify_list_mail_threads",
  searchMailThreads: "repackify_search_mail_threads",
  getMailThread: "repackify_get_mail_thread",
  getMailAttachment: "repackify_get_mail_attachment",
  searchRecords: "repackify_search_records",
  reportMissingCapability: "repackify_report_missing_capability",
} as const;

export const AGENT_OPS_MCP_TOOLS = {
  prepareResendDocument: "repackify_prepare_resend_document",
  resendDocument: "repackify_resend_document",
  prepareAddOrderNote: "repackify_prepare_add_order_note",
  addOrderNote: "repackify_add_order_note",
  prepareAdvanceOrderStage: "repackify_prepare_advance_order_stage",
  advanceOrderStage: "repackify_advance_order_stage",
  prepareDraftMailReply: "repackify_prepare_draft_mail_reply",
  draftMailReply: "repackify_draft_mail_reply",
  prepareLinkMailThread: "repackify_prepare_link_mail_thread",
  linkMailThread: "repackify_link_mail_thread",
  prepareNotifyStaffMember: "repackify_prepare_notify_staff_member",
  notifyStaffMember: "repackify_notify_staff_member",
  getStaffNotification: "repackify_get_staff_notification",
} as const;

export const SUPPORT_AGENT_MCP_TOOL_NAMES = Object.values(
  SUPPORT_AGENT_MCP_TOOLS,
);

export const AGENT_CONTEXT_MCP_TOOL_NAMES = Object.values(
  AGENT_CONTEXT_MCP_TOOLS,
);

export const AGENT_OPS_MCP_TOOL_NAMES = Object.values(AGENT_OPS_MCP_TOOLS);

export const AGENT_MCP_TOOL_NAMES = [
  ...SUPPORT_AGENT_MCP_TOOL_NAMES,
  ...AGENT_CONTEXT_MCP_TOOL_NAMES,
  ...AGENT_OPS_MCP_TOOL_NAMES,
];
